import { getJSONFromScript } from './lib/server_params'

import { createHandler as createNumberHandler } from 'griffin-paypal/number'
import { createHandler as createNameHandler } from 'griffin-paypal/name'
import { createHandler as createDateHandler } from 'griffin-paypal/date'

export function getGriffinRules() {
  return getJSONFromScript('griffin')
}

const rules = getGriffinRules()

const numberHandler = createNumberHandler(rules.clientMetadata)
const nameHandler = createNameHandler(rules.clientMetadata)
const dateHandler = createDateHandler(rules.clientMetadata)

export function getDefaultCurrencyCode() {
  return rules.defaultCurrencyCode
}

export function getCountryCurrencyCodes() {
  return rules.countryCurrencyCodes
}

function getCurrencyFormatPart(parts, part) {
  return (parts.find(p => p.type === part) || {}).value || ''
}

export function getCurrencySymbol(currency) {
  const parts = numberHandler.formatCurrency(
    { value: 0, currency },
    numberHandler.CURRENCY_FORMAT.parts()
  )
  return getCurrencyFormatPart(parts, 'symbol')
}

export function formatAmount(value) {
  return numberHandler.formatNumber(value, numberHandler.NUMBER_FORMAT)
}

export function formatPercent(value) {
  return numberHandler.formatNumber(value, numberHandler.NUMBER_FORMAT_PERCENT)
}

export function formatMoney(
  currency,
  value,
  {
    removeTrailingZeros = false,
    showCurrencySymbol = true,
    hideCurrencyCode = false,
  } = {}
) {
  if (value == null) {
    return ''
  }

  if (!showCurrencySymbol) {
    const parts = numberHandler.formatCurrency(
      { value, currency },
      numberHandler.CURRENCY_FORMAT.parts()
    )
    return parts
      .filter(p => p.type !== 'separator' && p.type !== 'symbol')
      .map(p => p.value)
      .join(getCurrencyFormatPart(parts, 'separator'))
  }

  const currencyFormat =
    shouldUseIntlFormat(currency) && !hideCurrencyCode
      ? numberHandler.CURRENCY_FORMAT_SYMBOLISOCURRENCY
      : numberHandler.CURRENCY_FORMAT

  if (removeTrailingZeros) {
    currencyFormat.minimumFractionDigits = 0
  }

  return numberHandler.formatCurrency({ currency, value }, currencyFormat)
}

export function shouldUseIntlFormat(currency) {
  return getCountryCurrencyCodes().indexOf(currency) < 0
}

export function isSymbolBeforeValue(currency) {
  const parts = numberHandler.formatCurrency(
    { value: 0, currency },
    numberHandler.CURRENCY_FORMAT.parts()
  )
  return (
    parts.findIndex(p => p.type === 'symbol') <
    parts.findIndex(p => p.type === 'value')
  )
}

export function normalizeDate(
  dateString,
  options = { style: dateHandler.DATE_FORMAT_SHORT }
) {
  return dateHandler.normalizeDate(dateString, {
    timeZone: getTimeZone(),
    ...options,
  })
}

export function shortDatePatternLabel() {
  return dateHandler.dateInputMask().toUpperCase()
}

export function shortDateInputMaskParts(
  options = { style: dateHandler.DATE_FORMAT_SHORT }
) {
  return dateHandler.dateInputMask({
    parts: true,
    ...options,
  })
}

export function shortDateInputPattern() {
  return rules.dateRules.shortDateInputPattern
}

export function shortDateFormat() {
  return rules.dateRules.shortDateFormat
}

export function shortDateRegex() {
  return rules.dateRules.shortDateRegex
}

export function getInformalName(name) {
  return nameHandler.formatName(name, {
    style: nameHandler.NAME_FORMAT_INFORMAL,
  })
}

export function getFullName(name) {
  return nameHandler.formatName(name, { style: nameHandler.NAME_FORMAT_FULL })
}

export function getDecimalPrecision(currencyCode = getDefaultCurrencyCode()) {
  return rules.clientMetadata.griffin.number.currencyInfo[currencyCode].digits
}

export function getDecimalSeparator() {
  return rules.decSep
}

export function formatDateShort(date) {
  return formatDate(date, dateHandler.DATE_FORMAT_SHORT)
}

export function formatDateExtraShort(date) {
  return formatDate(date, 'Md')
}

export function formatDateMedium(date) {
  return formatDate(date, dateHandler.DATE_FORMAT_MEDIUM)
}

/**
 * Helper function to get timezone from griffin.
 */
export function getTimeZone() {
  return (dateHandler && dateHandler.getTimeZone()) || 'Etc/UTC'
}

export function formatDate(date, style) {
  if (!(date instanceof Date)) {
    date = new Date(date)
  }

  return dateHandler.formatDate(date, {
    timeZone: getTimeZone(),
    style,
  })
}

export function postalCodeRegex() {
  return rules.postalCodeRegex
}
